/**
 * Pickup Hungary office staff signing in to the portal — the dispatch
 * board's only door.
 *
 * There is no dispatcher enrolment link any more. Dispatchers are people who
 * already have a Pickup Hungary dashboard account, so they use it: this
 * screen drives the SAME endpoints the dashboard's own login page drives
 * (`/login`, and the MFA challenge behind it), through the same
 * `AuthController` and the same `SolveChallenge` — MFA included, unchanged.
 * Nothing about authentication is reimplemented here; what is new is only
 * where the resulting token is put.
 *
 * That token then becomes the portal session (`storeStaffSession`), which
 * asks the API who it is before believing it. The API answers `dispatcher`
 * only for someone holding the Operators permission, so a colleague without
 * it is told so HERE, in a sentence, rather than being dropped onto a board
 * that cannot load.
 *
 * Drivers and operators never come through this screen: their enrolment link
 * is untouched, and the gate points them at the office as it always did.
 */
import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { login } from '../../Controllers/AuthController';
import { getToken } from '../../Services/BearerToken';
import { getPortalPrincipal, hasPortalSession, storeStaffSession } from '../../Services/PortalApi';
import { PORTAL_USER_KINDS } from '../../config/config';
import SolveChallenge from '../Login/Components/SolveChallenge';
import PortalShell from './Components/PortalShell';
import BrandLogo from './Components/BrandLogo';

/**
 * What someone is told when their password was right but the board still
 * would not open.
 *
 * No permission gates the board any more — any Pickup Hungary account
 * reaches it — so this is no longer "not for you". What is left is a
 * session the API would not accept, and the realistic cause is on our side
 * (an access token rather than an ID token; the session endpoint not
 * configured with the admin pool id, which makes it refuse every staff
 * sign-in). So the copy says what is true: their credentials were fine,
 * retrying will not help, and somebody here has to look at it.
 *
 * Telling them to reset a password that worked, or to ask for a permission
 * that no longer exists, would both send them somewhere useless.
 */
export const BOARD_DID_NOT_OPEN = 'Your password was accepted, but the dispatch board could not open your session. '
  + 'This is not something you can fix — please tell the office.';

/**
 * A 401 or 403 from the session call means the token was not accepted;
 * anything else is a plain failure worth showing as it came.
 */
const refusalMessage = (error) => {
  const status = error?.response?.status;

  if (status === 403 || status === 401) return BOARD_DID_NOT_OPEN;

  return error?.response?.data?.message || error?.message || 'Could not sign in. Please try again.';
};

export default function PortalStaffLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  // Already signed in: the landing screen knows where each kind belongs.
  const signedIn = hasPortalSession();

  /**
   * The step every successful sign-in ends on, whether or not MFA was in the
   * way: take the dashboard token AuthController has just stored and ask the
   * portal to adopt it. Throwing here is what `SolveChallenge`'s MFA
   * components already show as an error, so a refusal lands on the form
   * instead of navigating to a board the person cannot open.
   */
  const adoptDashboardSession = async () => {
    try {
      await storeStaffSession(getToken());
    } catch (err) {
      throw new Error(refusalMessage(err));
    }
    navigate('/portal');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setNotice('');

    try {
      const result = await login(email.trim(), password);

      if (result === true) {
        await adoptDashboardSession();
        return;
      }

      if (result && result.challengeName) {
        setChallenge(result);
        return;
      }

      setError('Could not sign in. Please check your email and password.');
    } catch (err) {
      setError(err.message || 'Could not sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (signedIn && getPortalPrincipal()?.kind === PORTAL_USER_KINDS.DISPATCHER) {
    return <Navigate to="/portal/dispatcher" replace />;
  }

  return (
    <PortalShell dark title="Pickup Hungary · Dispatch">
      <div className="pp-topbrand">
        <BrandLogo variant="white" height={48} />
      </div>

      <div className="pp-gate">
        <h1 className="pp-gate__title">Sign in to the dispatch board</h1>
        <p className="pp-gate__body">
          Use your Pickup Hungary account — the same email and password as the dashboard.
        </p>

        {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}
        {notice ? <div className="alert alert-success" role="alert">{notice}</div> : null}

        {challenge ? (
          <SolveChallenge
            name={challenge.challengeName}
            session={challenge.session}
            userIdForSRP={challenge.userIdForSRP || email.trim()}
            username={email.trim()}
            setError={setError}
            setChallengeResponse={setChallenge}
            setSuccess={setNotice}
            // The portal has no "isAuthed" of its own — the session in
            // storage is the whole state — so this only has to not be a
            // no-op prop the shared component can trip over.
            setIsAuthed={() => {}}
            onLoginSuccess={adoptDashboardSession}
          />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-3 text-start">
              <label htmlFor="portal-staff-email" className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                id="portal-staff-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div className="mb-3 text-start">
              <label htmlFor="portal-staff-password" className="form-label">Password</label>
              <input
                type="password"
                className="form-control"
                id="portal-staff-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            <button type="submit" disabled={loading} className="btn btn-primary w-100">
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Signing in…
                </>
              ) : 'Sign in'}
            </button>
          </form>
        )}

        <p className="pp-gate__body mt-4">
          <Link to="/portal">Are you a driver? Go back</Link>
        </p>
      </div>
    </PortalShell>
  );
}
